import { Color, Data3DTexture, NearestFilter, RGBAFormat, UnsignedByteType } from 'three';
import c64 from '../../content/palettes/c64.json';
import core from '../../content/palettes/core.json';
import foods from '../../content/palettes/foods.json';
import powerups from '../../content/palettes/powerups.json';
import { hexToRgb8, nearestPaletteIndex, rgb8ToHex, rgb8ToOklab, type Rgb8 } from './color';
import { c64FigureRamps } from './spriteLook';

/** Whose colours the figures wear: the arcade's (default, the owner's choice 2026-09-23) or the C64 sprites'. */
export type FigureColours = 'c64' | 'arcade';

/** A colour reference: `ramp.index` (0 = darkest) or `ramp.lo | mid | hi`. */
export type ColorRef = string;

/**
 * What one level looks like (05 §4): its blocks, the wall behind the stage, and the colour
 * the back of the stage fades into. Each level of the original had its own block design and
 * colours, so each gets its own look, and with it its own quantisation LUT.
 */
export interface LevelLook {
  /** Block ramp, dark -> light. */
  block: readonly string[];
  /** Second block ramp for the pattern's accent faces (mortar, studs, bevels). */
  blockAlt: readonly string[];
  /**
   * The block colours as they look in shadow. No builder uses these; they are in the palette
   * so the lock keeps a shadowed block in its own hue instead of snapping it to the nearest
   * colour of some other ramp (a yellow platform in shadow went Bub-green without them).
   */
  blockShadow: readonly string[];
  /** The back wall of the stage, dark -> light. */
  backdrop: readonly string[];
  /** What the far back fades into; also the clear colour. */
  fog: string;
}

/** Lighting and the post-lock grade (05 §7). */
export interface Grade {
  name: string;
  sunDir: [number, number, number];
  sunColor: string;
  sunIntensity: number;
  skyColor: string;
  groundColor: string;
  hemiIntensity: number;
  fogAmount: number;
  tint: string;
  tintAmount: number;
  valueScale: number;
}

export const LUT_SIZE = 32;

export class Palette {
  readonly ramps: Readonly<Record<string, readonly string[]>>;
  readonly fog: string;
  private readonly cache = new Map<ColorRef, Color>();

  constructor(look: LevelLook, figures: FigureColours = 'arcade') {
    this.ramps = {
      ...core.ramps,
      // The item models' own colours (M2: foods and power-ups each keep their ramps in a file of their own).
      ...(foods.ramps as Record<string, string[]>),
      ...(powerups.ramps as Record<string, string[]>),
      ...(figures === 'c64' ? c64FigureRamps() : {}),
      block: look.block,
      blockAlt: look.blockAlt,
      blockShadow: look.blockShadow,
      backdrop: look.backdrop,
    };
    this.fog = look.fog;
  }

  /** Hex string for a colour reference. Throws on an unknown ramp so typos fail loudly. */
  hex(ref: ColorRef): string {
    const [rampName, step = 'mid'] = ref.split('.');
    const ramp = rampName === undefined ? undefined : this.ramps[rampName];
    if (!ramp || ramp.length === 0) throw new Error(`unknown palette ramp in "${ref}"`);
    let index: number;
    if (step === 'lo') index = 0;
    else if (step === 'hi') index = ramp.length - 1;
    else if (step === 'mid') index = Math.floor((ramp.length - 1) / 2);
    else index = Math.min(ramp.length - 1, Math.max(0, Number.parseInt(step, 10) || 0));
    return ramp[index] ?? '#ff00ff';
  }

  /** Linear-space three.js colour (shared instance: do not mutate). */
  color(ref: ColorRef): Color {
    let c = this.cache.get(ref);
    if (!c) {
      c = new Color(this.hex(ref));
      this.cache.set(ref, c);
    }
    return c;
  }

  /** Every colour in the active palette, sRGB 8-bit. */
  all(): Rgb8[] {
    const out: Rgb8[] = [];
    for (const ramp of Object.values(this.ramps)) for (const hex of ramp) out.push(hexToRgb8(hex));
    out.push(hexToRgb8(this.fog));
    return out;
  }

  /**
   * Quantisation LUT for this palette. `extra` adds colours that must survive the lock too -
   * the next level's ramps while both are on screen in the drop between levels.
   */
  buildLut(extra: readonly Rgb8[] = []): Data3DTexture {
    return buildLut([...this.all(), ...extra]);
  }
}

/**
 * An sRGB-indexed 3D texture whose every cell holds the nearest of `colours` (nearest in
 * OKLab), sampled with nearest filtering by the post pass. The 1987 look is this built from
 * the sixteen C64 colours alone.
 */
export function buildLut(colours: readonly Rgb8[]): Data3DTexture {
  const labs = colours.map(rgb8ToOklab);
  const n = LUT_SIZE;
  const data = new Uint8Array(n * n * n * 4);
  let i = 0;
  for (let b = 0; b < n; b++) {
    for (let g = 0; g < n; g++) {
      for (let r = 0; r < n; r++) {
        const cell: Rgb8 = [
          Math.round((r / (n - 1)) * 255),
          Math.round((g / (n - 1)) * 255),
          Math.round((b / (n - 1)) * 255),
        ];
        const hit = colours[nearestPaletteIndex(rgb8ToOklab(cell), labs)] ?? cell;
        data[i++] = hit[0];
        data[i++] = hit[1];
        data[i++] = hit[2];
        data[i++] = 255;
      }
    }
  }
  const tex = new Data3DTexture(data, n, n, n);
  tex.format = RGBAFormat;
  tex.type = UnsignedByteType;
  tex.minFilter = NearestFilter;
  tex.magFilter = NearestFilter;
  tex.unpackAlignment = 1;
  tex.needsUpdate = true;
  return tex;
}

/** The sixteen C64 colours, sRGB 8-bit (Colodore). */
export const C64_COLOURS: readonly Rgb8[] = c64.colours.map(hexToRgb8);

let c64LutTexture: Data3DTexture | null = null;

/** The 1987 look's LUT (05 §2.6): the sixteen C64 colours alone. Made once and shared; never dispose it. */
export function c64Lut(): Data3DTexture {
  c64LutTexture ??= buildLut(C64_COLOURS);
  return c64LutTexture;
}

/** The C64 colour nearest `hex` (in OKLab), as "#rrggbb": for what the 1987 look draws outside the LUT. */
export function nearestC64(hex: string): string {
  const labs = C64_COLOURS.map(rgb8ToOklab);
  const hit = C64_COLOURS[nearestPaletteIndex(rgb8ToOklab(hexToRgb8(hex)), labs)];
  return hit ? rgb8ToHex(hit) : hex;
}
